/**
 * The body of the hybrid-AHU Info window (requested 2026-09-30) — the "How to"
 * for the Hybrid Configuration board, transcribed verbatim from the request.
 *
 * Copy and the filter over it: no board state, no data from the server, no
 * props but the query. It is split out of `HybridInfoPopup` so the window
 * chrome (drag / minimize / close / geometry) stays readable next to ~200
 * lines of copy, and so the copy can be edited without touching any of it.
 *
 * The window's own title block — "How to - Hybrid Air Handling Unit" — used to
 * open this file. It was dropped on request 2026-09-30: the title moved into
 * the header, so the body starts straight at the first heading.
 *
 * Markup follows the other "How to" ports on the board (ComboLouverHowTo,
 * FilterSelector's and FanSelector's how-tos): `<ol>` where the source
 * numbers its items, `<ul>` where it bullets them, `<b>` for the run-in
 * lead-ins, and `<code>` for the two literal values the text quotes
 * (`1:2:4` and the Y-Offset formula).
 *
 * SEARCH (requested 2026-11-02). The copy is a flat run of `<h4>` + prose, so
 * "show the whole section a hit lands in" needs the section boundaries to be
 * data rather than document order: `SECTIONS` below is that list, one entry per
 * heading, and the component renders whichever entries match. The haystack is
 * derived from the JSX itself (`nodeText`) rather than written out a second
 * time, so editing the copy cannot leave a section unsearchable.
 */

import { Fragment, isValidElement, type ReactNode } from 'react';

interface HybridInfoSection {
  heading: string;
  body: ReactNode;
}

const SECTIONS: HybridInfoSection[] = [
  {
    heading: 'Standard vs Hybrid Air Handling Unit',
    body: (
      <ol>
        <li>User Skill, Standard or Power, is set by the CMS Administrator.</li>
        <li>
          Standard air handling units may be designed, viewed, and edited by Standard and Power
          users.
        </li>
        <li>
          Hybrid air handling units may be designed, viewed, and edited exclusively by Power users
          and viewed by Standard users.
        </li>
        <li>
          The type of an air handling unit, Standard or Hybrid (for Power users), is permanently
          set when adding the first section in the unit and may not be modified at a later time.
        </li>
      </ol>
    ),
  },
  {
    heading: 'X, Y, Z Coordinates',
    body: (
      <ul>
        <li>
          X Origin 0.00 is the upstream edge of section 1:1:1 (Section at Row 1, Level 1, Position
          1).
        </li>
        <li>
          Y Origin 0.00 is the right-side edge of section 1:1:1 (Section at Row 1, Level 1,
          Position 1).
        </li>
        <li>
          Z Origin 0.00 is the floor of section 1:1:1 (Section at Row 1, Level 1, Position 1).
        </li>
      </ul>
    ),
  },
  {
    heading: 'Section Nomenclature',
    body: (
      <>
        <p>
          <code>1:2:4</code>
        </p>
        <ul>
          <li>The first digit 1 stands for the section row.</li>
          <li>The second digit 2 stands for the section level.</li>
          <li>The third digit 4 stands for the section position in the given row.</li>
        </ul>
      </>
    ),
  },
  {
    heading: 'Per Section Airflow Direction',
    body: (
      <>
        <p>
          The airflow direction of each individual section is set by the user, either left to right
          or right to left.
        </p>
        <p>
          When a new section is added, the default airflow direction is set equal to the airflow
          direction of the preceding section in the same row.
        </p>
      </>
    ),
  },
  {
    heading: 'X-Offset',
    body: (
      <ol>
        <li>
          The X-Offset of any section is the distance of its upstream edge from X = 0.00, taking
          into account the airflow direction of the section.
        </li>
        <li>
          When a new section is added, the system automatically sets its X-Offset dimension to be
          adjacent to the preceding section by default, taking into account its airflow direction.
        </li>
      </ol>
    ),
  },
  {
    heading: 'Y-Offset',
    body: (
      <>
        <p>Available options:</p>
        <ul>
          <li>Centered</li>
          <li>Left Aligned</li>
          <li>Right Aligned</li>
          <li>User Defined Left</li>
          <li>User Defined Right</li>
        </ul>

        <p>
          <b>Centered:</b> When a new section is added in an existing row, meaning a row where
          there is already one or more sections, the Y-Offset of the new section in the Data Bar
          shall be set to Centered by default.
        </p>
        <p>
          When a section is Centered, its centerline (along its width) is aligned with the
          centerline of the previous section in the same row.
        </p>
        <p>
          When a new section is added in a new row, the Y-Offset of the section in the Data Bar
          shall be set to User Defined Left by default, and the Offset value shall be set so that
          the new section is positioned immediately to the left of the section nearest to its
          upstream edge.
        </p>
        <p>The Offset position formula is:</p>
        <p>
          <code>nearest section width / 2 + new section width / 2</code>
        </p>
        <p>
          For a new section in a new row, only <b>User Defined Left</b> and <b>User Defined Right</b>{' '}
          shall be available.
        </p>

        <p>
          <b>Left Aligned:</b> For a section (A) in a row with one or more other sections in the
          row, the edge of section (A) shall be aligned with the left edge of the preceding
          section.
        </p>
        <p>
          When a Left-Aligned section and its previous section in the same row have identical
          widths, Left-Aligned effectively gives the same result as Centered.
        </p>
        <p>
          When they have different widths, the left edges of the two sections are aligned and the
          offset is visible.
        </p>

        <p>
          <b>Right Aligned:</b> For a section (A) in a row with one or more other sections in the
          row, the edge of section (A) shall be aligned with the right edge of the preceding
          section.
        </p>
        <p>
          When a Right-Aligned section and its previous section in the same row have identical
          widths, Right-Aligned effectively gives the same result as Centered.
        </p>
        <p>
          When they have different widths, the right edges of the two sections are aligned and the
          offset is visible.
        </p>

        <p>
          <b>User Defined Left:</b> The Offset value of section (A) is the distance from the
          centerline of preceding section (B) to the centerline of section (A), in the left
          direction of preceding section (B).
        </p>
        <p>
          <b>User Defined Right:</b> The Offset value of section (A) is the distance from the
          centerline of preceding section (B) to the centerline of section (A), in the right
          direction of preceding section (B).
        </p>
      </>
    ),
  },
  {
    heading: 'Auto-Alignment in X Direction',
    body: (
      <>
        <p>
          When the AHU is auto-aligned in the X direction, all sections with common floor heights
          (from ground 0) are automatically moved left or right as necessary to bring them
          together, while maintaining the floor heights of all sections.
        </p>
        <p>
          The exception is when a section is positioned in space with another section below its
          upstream edge. In this case, the top section being aligned is lowered to sit directly on
          top of the section below its upstream edge.
        </p>
      </>
    ),
  },
  {
    heading: 'Casing, Upstream, Downstream Walls',
    body: (
      <>
        <p>
          The user has the ability to check or uncheck whether any given section has a wall at any
          of the four faces of the section.
        </p>
        <p>
          When a new section is added downstream or upstream of another section in the same row,
          the upstream and downstream common adjacent walls are removed from both sections. The
          walls may be added back by the user.
        </p>
        <p>
          When the airflow direction of an existing section is reversed, the upstream and
          downstream common adjacent walls are removed from both sections. The walls may be added
          back by the user.
        </p>
      </>
    ),
  },
  {
    heading: 'Casing Left, Right Walls',
    body: (
      <p>
        The user has the ability to check or uncheck whether a given section has a left or right
        wall.
      </p>
    ),
  },
  {
    heading: 'Floor with Under-Floor Insulation and Under-Floor Liner',
    body: (
      <p>
        The user has the ability to check or uncheck whether a given section has a floor. When the
        floor of a section is removed, the under-floor insulation and under-floor liner are removed
        with it.
      </p>
    ),
  },
];

/**
 * Every string inside a chunk of JSX, space-joined. Elements are walked through
 * their `children`, so a phrase broken across a `<b>` ("only User Defined Left
 * and …") still reads as one run of words; anything that is not text (`null`,
 * booleans, functions) contributes nothing.
 */
const nodeText = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).filter(Boolean).join(' ');
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return '';
};

/**
 * One run of whitespace per gap, lowercased. Applied to both sides of the
 * comparison: JSX text already carries the source's line breaks and
 * indentation, and `nodeText`'s own joins add more, so "only User Defined Left"
 * — a phrase that spans an inline `<b>` — would otherwise miss.
 */
const squash = (text: string): string => text.replace(/\s+/g, ' ').trim().toLowerCase();

/** Heading + copy of each section, squashed once at module load. */
const HAYSTACKS = SECTIONS.map((s) => squash(`${s.heading} ${nodeText(s.body)}`));

/**
 * The sections a query matches — case-insensitive, partial, over heading *and*
 * copy, and a section matches whole or not at all. An empty query matches
 * everything. Exported for `hybridInfoContent.test.ts`.
 */
export const filterHybridInfoSections = (query: string): HybridInfoSection[] => {
  const q = squash(query);
  if (!q) return SECTIONS;
  return SECTIONS.filter((_, i) => HAYSTACKS[i].includes(q));
};

const HybridInfoContent = ({ query = '' }: { query?: string }) => {
  const sections = filterHybridInfoSections(query);

  if (!sections.length) {
    return <p className="hybrid-info-no-match">No results found.</p>;
  }

  return (
    <>
      {sections.map((section) => (
        <Fragment key={section.heading}>
          <h4 className="hybrid-info-heading">{section.heading}</h4>
          {section.body}
        </Fragment>
      ))}
    </>
  );
};

export default HybridInfoContent;
